import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CertificationsSection = () => {
  const certifications = [
    {
      title: "Certifié ISO 9001:2015",
      description: "Certification internationale du système de gestion de la qualité garantissant une prestation de services cohérente et une amélioration continue dans toutes les opérations.",
      icon: "Award",
      issuer: "Organisation Internationale de Normalisation",
      year: "2020"
    },
    {
      title: "Licence Commerciale RDC",
      description: "Entièrement licencié et enregistré auprès du Ministère du Commerce de la République Démocratique du Congo, autorisé à opérer dans les 12 secteurs d'activité.",
      icon: "FileCheck",
      issuer: "Ministère du Commerce RDC",
      year: "2015"
    },
    {
      title: "Accréditation Immobilière",
      description: "Agence immobilière certifiée avec autorisation complète pour la gestion immobilière, les ventes et les services de location dans les régions de Kinshasa et Lubumbashi.",
      icon: "Building2",
      issuer: "Conseil de Réglementation Immobilière RDC",
      year: "2017"
    },
    {
      title: "Conformité Sécurité IT",
      description: "Certifié pour les normes de protection des données et de cybersécurité, garantissant la sécurité des informations clients et la conformité aux meilleures pratiques internationales.",
      icon: "Shield",
      issuer: "Institut International de Cybersécurité",
      year: "2021"
    },
    {
      title: "Certification Environnementale",
      description: "Reconnu pour les pratiques commerciales durables et la responsabilité environnementale dans les opérations, la chaîne d'approvisionnement et la prestation de services.",
      icon: "Leaf",
      issuer: "Agence de Protection de l'Environnement RDC",
      year: "2022"
    },
    {
      title: "Prix d'Excellence Corporative",
      description: "Récipiendaire du Prix d'Excellence Corporative RDC pour contribution exceptionnelle au développement commercial et à l'innovation de services.",
      icon: "Trophy",
      issuer: "Chambre de Commerce RDC",
      year: "2023"
    }];


  const partnerships = [
    {
      name: "Partenaire Microsoft",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_147a12152-1768647653586.png",
      logoAlt: "Logo de partenaire technologique Microsoft affiché sur un écran numérique moderne avec marque corporative bleue"
    },
    {
      name: "Dell Technologies",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1887e8206-1766906383237.png",
      logoAlt: "Emblème de partenariat Dell Technologies sur affichage corporatif professionnel avec design argent et bleu"
    },
    {
      name: "HP Enterprise",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11c24d50f-1769301923201.png",
      logoAlt: "Badge de partenaire autorisé HP Enterprise sur écran de présentation d'affaires élégant"
    },
    {
      name: "Toyota Fleet",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1955a37fa-1769301924713.png",
      logoAlt: "Logo de partenariat Toyota Fleet Services sur affichage professionnel de l'industrie automobile"
    },
    {
      name: "Cisco Systems",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_10cda02f8-1769301924926.png",
      logoAlt: "Emblème de partenaire certifié Cisco Systems sur écran de technologie de réseau moderne"
    },
    {
      name: "SAP Business",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_198e95898-1769604374828.png",
      logoAlt: "Badge de partenariat SAP Business Solutions sur affichage de présentation de logiciel d'entreprise"
    }];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const partnerVariants = {
    hidden: { opacity: 0, scale: 0.85 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section className="py-20 lg:py-28 bg-slate-100 dark:bg-slate-900 relative overflow-hidden">
      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Gradient backgrounds */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-blue-900/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2.5 bg-blue-500/10 border border-blue-500/20 px-5 py-2.5 rounded-full mb-6">
            <Icon name="BadgeCheck" size={20} className="text-blue-400" />
            <span className="text-sm font-medium text-blue-600 dark:text-blue-300">Certifications & Conformité</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-5">
            Excellence Certifiée &{' '}
            <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
              Partenariats de Confiance
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Nos certifications et partenariats stratégiques démontrent notre engagement envers les normes de qualité internationales et une prestation de services fiable.
          </p>
        </motion.div>

        {/* Certifications Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16 lg:mb-24"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
        >
          {certifications?.map((cert, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-3xl p-10 hover:border-blue-500/30 hover:bg-slate-100 dark:hover:bg-slate-800/60 shadow-xl transition-all duration-500 group"
            >
              <div className="flex items-start gap-5 mb-5">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-500 shadow-lg shadow-blue-900/30">
                  <Icon name={cert?.icon} size={28} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    {cert?.title}
                  </h3>
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Calendar" size={14} className="text-amber-400" />
                    <span className="text-amber-400 font-medium">{cert?.year}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-5">
                {cert?.description}
              </p>

              <div className="pt-5 border-t border-slate-200 dark:border-slate-700/50">
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Icon name="Building" size={14} />
                  <span className="line-clamp-1">{cert?.issuer}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Partnerships Section */}
        <motion.div
          className="bg-gradient-to-r from-blue-900/20 to-slate-800/40 backdrop-blur-xl border border-white/5 rounded-[2.5rem] p-8 md:p-12 lg:p-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="text-center mb-10 lg:mb-14">
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-3">
              Partenariats{' '}
              <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
                Stratégiques
              </span>
            </h3>
            <p className="text-base text-slate-600 dark:text-slate-400">
              Collaborer avec les leaders de l'industrie pour fournir des solutions de classe mondiale
            </p>
          </div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 lg:gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
          >
            {partnerships?.map((partner, index) => (
              <motion.div
                key={index}
                variants={partnerVariants}
                className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 group flex items-center justify-center"
              >
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden bg-gray-50 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                    <Image
                      src={partner?.logo}
                      alt={partner?.logoAlt}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 group-hover:text-blue-700 transition-colors duration-300">
                    {partner?.name}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CertificationsSection;
